import FastForwardOutlinedIcon from "@mui/icons-material/FastForwardOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { UB_VIDEO_URL, UB_VIDEO_VOLUME } from "@/constants/campaignBuilder";
import { isSkipVisible } from "@/utils/unboxing-helper";
import GiftMessageModal from "./GiftMessageModal";
import RedemptionHeader from "./RedemptionHeader";
import UnboxingIntroLayout from "./UnboxingIntroLayout";
import UnboxingSkipButton from "./UnboxingSkipButton";

interface UnboxingProps {
  isMobile: boolean;
  headline: string;
  showGiftMessageButton: boolean;
  onReadMessage: () => void;
  /** Fired at the start of the Unwrap gesture. The recipient flow uses it to prime
   *  confetti audio (iOS needs the unlock inside the gesture). Optional — no-op if
   *  the caller doesn't pass one. */
  onUnwrap?: () => void;
  /** Increment to trigger video play + dissolve intro from outside (e.g. modal's Unwrap Gift). */
  playTrigger: number;
  skipUnboxing: boolean;
  customVideoUrl?: string;
  /** Mobile-only: when true, renders the gift message modal inside this container */
  giftMsgOpen?: boolean;
  giftMessage?: string;
  onCloseMessage?: () => void;
  onUnwrapMessage?: () => void;
  onVideoEnd?: () => void;
  /** Recipient route only: shows a "Skip video" control while the video plays. Unrelated to the
   *  campaign's `skipUnboxing`; builder previews omit it. */
  onSkip?: () => void;
  isFullscreen?: boolean;
  /** Play the unbox video with sound when true, muted otherwise. Requires a user
   *  gesture to start (the Unwrap tap). */
  withSound?: boolean;
  /** Partner/company logo shown beside the GfG logo in the header (recipient flow,
   *  from the campaign's landing config). Omitted by the builder preview. */
  partnerLogoUrl?: string;
  partnerLogoAlt?: string;
}

export default function Unboxing({
  isMobile,
  headline,
  showGiftMessageButton,
  onReadMessage,
  onUnwrap,
  playTrigger,
  skipUnboxing,
  customVideoUrl,
  giftMsgOpen,
  giftMessage,
  onCloseMessage,
  onUnwrapMessage,
  onVideoEnd,
  onSkip,
  isFullscreen = false,
  withSound = false,
  partnerLogoUrl,
  partnerLogoAlt,
}: UnboxingProps) {
  const [introVisible, setIntroVisible] = useState(true);
  // Video ended or Skip pressed. The ref twin guards re-entry within one task (a double press, or
  // a press racing `ended`), where the state is still stale.
  const [finished, setFinished] = useState(false);
  const finishedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Blob URLs don't support the #t= fragment; HTTP URLs use it to show the first frame
  const videoSrc = customVideoUrl ?? `${UB_VIDEO_URL}#t=0.1`;

  const startPlayback = useCallback(() => {
    onUnwrap?.();
    finishedRef.current = false;
    setFinished(false);
    setIntroVisible(false);
    const v = videoRef.current;
    if (!v) return;
    // Recipient flow plays with sound (unlocked by the Unwrap gesture); the builder
    // preview stays muted.
    if (withSound) {
      v.muted = false;
      v.volume = UB_VIDEO_VOLUME;
    }
    v.play().catch((err: unknown) => {
      // AbortError is expected when load() interrupts an in-flight play() — safe to ignore
      if (err instanceof DOMException && err.name === "AbortError") return;
      // Autoplay-with-sound can be blocked (e.g. not gesture-bound) — retry muted so
      // the video still plays rather than stranding the flow.
      if (withSound) {
        v.muted = true;
        v.play().catch(() => {});
        return;
      }
      throw err;
    });
  }, [onUnwrap, withSound]);

  /** Marks this playback finished; false if it already was (ended or skipped). */
  function finish(): boolean {
    if (finishedRef.current) return false;
    finishedRef.current = true;
    setFinished(true);
    return true;
  }

  function handleVideoEnded() {
    // Deliberately no rewind and no intro restore: the inline preview (no onVideoEnd)
    // must rest on the video's final frame instead of replaying it (GFG-265).
    if (finish()) onVideoEnd?.();
  }

  function handleSkip() {
    if (!finish()) return;
    videoRef.current?.pause();
    onSkip?.();
  }

  // Reload the video element when the source changes so the new file plays
  // biome-ignore lint/correctness/useExhaustiveDependencies: customVideoUrl is an intentional trigger; video.load() implicitly uses the updated src attribute
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.load();
    finishedRef.current = false;
    setFinished(false);
    setIntroVisible(true);
  }, [customVideoUrl]);

  // Pause video and reset intro when skip is toggled on
  useEffect(() => {
    if (skipUnboxing) {
      videoRef.current?.pause();
      finishedRef.current = false;
      setFinished(false);
      setIntroVisible(true);
    }
  }, [skipUnboxing]);

  // Fired ONLY when playTrigger is INCREMENTED (e.g. "Unwrap Gift" from the modal),
  // never on mount. Without the mount guard, returning to the unboxing tab while
  // playTrigger is already > 0 (from an earlier Unwrap) would auto-play the video —
  // it should only play on an explicit Unwrap.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (playTrigger > 0) startPlayback();
  }, [playTrigger, startPlayback]);

  return (
    <div
      className="flex flex-col overflow-hidden relative"
      style={
        isFullscreen
          ? { position: "absolute", inset: 0, background: "var(--color-light-dust)" }
          : {
              width: isMobile ? 360 : "100%",
              background: "var(--color-light-dust)",
              borderRadius: isMobile ? 8 : 0,
              boxShadow: isMobile ? "0 2px 12px rgba(0,0,0,0.06)" : "none",
            }
      }
    >
      {/* GFG top bar — floats over the video (z9, above the z8 intro overlay) so
          the intro content centers in the full height rather than being pushed
          below an in-flow bar. Shared with the landing/collection header. The builder's
          inline preview pins the logo size to its frame; fullscreen follows the viewport. */}
      <RedemptionHeader
        floating
        partnerLogoUrl={partnerLogoUrl}
        partnerLogoAlt={partnerLogoAlt}
        isMobile={isFullscreen ? undefined : isMobile}
      />

      {/* Video area. Fullscreen (recipient route, any device) fills the viewport
          via flex:1. Only the builder's INLINE (non-fullscreen) mobile preview
          keeps the phone-frame calc height — it sits in a scroll area with ~200px
          of surrounding builder chrome. */}
      <div
        className="relative overflow-hidden"
        style={
          isFullscreen
            ? { flex: 1, minHeight: 0, background: "var(--color-light-dust)" }
            : isMobile
              ? { minHeight: "calc(100dvh - 200px)", background: "var(--color-light-dust)" }
              : { flex: 1, minHeight: 460, background: "var(--color-light-dust)" }
        }
      >
        {/* Background video — hidden (not removed) so it stays preloaded */}
        <video
          ref={videoRef}
          src={videoSrc}
          muted
          playsInline
          preload="metadata"
          onEnded={handleVideoEnded}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            zIndex: 0,
            visibility: skipUnboxing ? "hidden" : "visible",
          }}
        />

        {/* Intro message overlay — fades out when Unwrap is clicked. Fully
            opaque (not 0.97) so the full-opacity video behind is masked during
            the intro — matches the prototype, where the video only appears after
            the reveal (no faint video bleeding through). */}
        {!skipUnboxing && (
          <div
            className="absolute inset-0 flex items-center justify-center overflow-hidden"
            style={{
              background: "#fff",
              zIndex: 8,
              opacity: introVisible ? 1 : 0,
              pointerEvents: introVisible ? "auto" : "none",
              transition: "opacity 0.8s ease",
            }}
          >
            <div
              style={{
                width: isMobile ? "100%" : 920,
                maxWidth: "100%",
                padding: isMobile ? "32px 24px" : "5% 48px 40px",
                boxSizing: "border-box",
              }}
            >
              <UnboxingIntroLayout
                isMobile={isMobile}
                headline={headline}
                showGiftMessageButton={showGiftMessageButton}
                onUnwrap={startPlayback}
                onReadMessage={onReadMessage}
                visible={introVisible}
              />
            </div>
          </div>
        )}

        {/* Skip unboxing overlay — mirrors prototype's .ub-skipped-overlay */}
        {skipUnboxing && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center"
            style={{
              background: "var(--color-light-dust)",
              zIndex: 10,
            }}
          >
            <FastForwardOutlinedIcon sx={{ fontSize: 40, color: "var(--color-smoke)" }} />
            <span
              className="font-lato"
              style={{
                fontSize: 14,
                color: "var(--color-graphite)",
                maxWidth: 260,
                lineHeight: 1.5,
              }}
            >
              Unboxing video is skipped.
              <br />
              Recipients will go directly to gift selection.
            </span>
          </div>
        )}

        {isSkipVisible({ optedIn: !!onSkip, introVisible, finished, skipUnboxing }) && (
          <UnboxingSkipButton onSkip={handleSkip} isMobile={isMobile} />
        )}
      </div>

      {/* Mobile gift message modal — rendered inside the phone frame */}
      {isMobile && giftMsgOpen && onCloseMessage && onUnwrapMessage && (
        <GiftMessageModal
          isMobile
          message={giftMessage}
          onClose={onCloseMessage}
          onUnwrap={onUnwrapMessage}
        />
      )}
    </div>
  );
}
